import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import type { ButtonVariant } from '../Button';
import { Button } from '../Button';
import { Tooltip } from '../Tooltip';

import {
  StyledModalOverlay,
  StyledModal,
  StyledModalHeader,
  StyledModalHeading,
  StyledModalContent,
  StyledCloseModalButton,
  StyledModalFooter,
  StyledModalSubheading,
} from './styles';

export type ModalButtonProps = {
  onClick?: () => void;
  children?: ReactNode;
  variant?: ButtonVariant;
  'data-automation'?: string;
  disabled?: boolean;
  tooltipContent?: ReactNode;
  loading?: boolean;
};

export type ModalProps = {
  isOpen: boolean;
  heading?: string | null;
  subheading?: string | null;
  hasCloseBtn?: boolean;
  onClose?: () => void;
  onCloseFinish?: () => void;
  children: ReactNode;
  confirmButtonProps?: ModalButtonProps;
  cancelButtonProps?: ModalButtonProps;
  closeOnClickOutside?: boolean;
  footerJustifyContent?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around';
  contentHorizontalPadding?: number;
  className?: string;
  footerLeftText?: string | ReactNode;
};

const animateAppearance = (element: HTMLElement, type: 'appearing' | 'disappearing') => {
  const direction = type === 'appearing' ? 'normal' : 'reverse';
  return element.animate([{ opacity: 0 }, { opacity: 1 }], {
    duration: 300,
    easing: 'ease-in-out',
    fill: 'both',
    direction,
  });
};

export const Modal = (props: ModalProps) => {
  const {
    heading,
    subheading,
    onClose,
    hasCloseBtn = true,
    confirmButtonProps,
    cancelButtonProps,
    isOpen,
    closeOnClickOutside = true,
    className,
    footerLeftText,
  } = props;
  const [internalIsOpen, setInternalIsOpen] = useState(isOpen);
  const overlayRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!overlayRef.current) {
      return;
    }

    if (!isOpen) {
      const animation = animateAppearance(overlayRef.current, 'disappearing');

      animation.onfinish = () => {
        setInternalIsOpen(false);
        props.onCloseFinish?.();
      };

      return;
    }

    setInternalIsOpen(isOpen);
    animateAppearance(overlayRef.current, 'appearing');
  }, [isOpen]);

  const shouldListen = isOpen && closeOnClickOutside;
  useOnClickOutside(modalRef, () => onClose?.(), shouldListen);

  const hasCloseButton = useMemo(() => {
    return hasCloseBtn && typeof onClose === 'function';
  }, [hasCloseBtn, onClose]);

  if (!isOpen && !internalIsOpen) {
    return null;
  }

  return (
    <StyledModalOverlay ref={overlayRef}>
      <StyledModal ref={modalRef} data-automation="Modal" className={className}>
        {hasCloseButton ? (
          <StyledCloseModalButton data-automation="Close" onClick={onClose}>
            <SWReactIcons iconName="close" size="xs" iconColor={colorsPalettes.carbon[250]} />
          </StyledCloseModalButton>
        ) : null}
        {heading ? (
          <StyledModalHeader hasCloseBtn={hasCloseButton}>
            <StyledModalHeading data-automation="ModalHeader">{heading}</StyledModalHeading>
          </StyledModalHeader>
        ) : null}
        {subheading ? <StyledModalSubheading>{subheading}</StyledModalSubheading> : null}
        <StyledModalContent horizontalPadding={props.contentHorizontalPadding}>{props.children}</StyledModalContent>
        {cancelButtonProps || confirmButtonProps || footerLeftText ? (
          <StyledModalFooter justifyContent={props.footerJustifyContent} data-automation="ModalFooter">
            {footerLeftText ? footerLeftText : null}
            {cancelButtonProps ? (
              <Tooltip enabled={!!cancelButtonProps.tooltipContent} content={cancelButtonProps.tooltipContent}>
                <Button
                  onClick={cancelButtonProps.onClick}
                  variant={cancelButtonProps.variant}
                  data-automation={cancelButtonProps['data-automation']}
                  disabled={cancelButtonProps.disabled}
                >
                  {cancelButtonProps.children}
                </Button>
              </Tooltip>
            ) : null}

            {confirmButtonProps ? (
              <Tooltip enabled={!!confirmButtonProps.tooltipContent} content={confirmButtonProps.tooltipContent}>
                <Button
                  onClick={confirmButtonProps.onClick}
                  variant={confirmButtonProps.variant}
                  data-automation={confirmButtonProps['data-automation']}
                  disabled={confirmButtonProps.disabled}
                  isLoading={confirmButtonProps.loading}
                >
                  {confirmButtonProps.children}
                </Button>
              </Tooltip>
            ) : null}
          </StyledModalFooter>
        ) : null}
      </StyledModal>
    </StyledModalOverlay>
  );
};
